/**
 * RegisterPage — the residence's front gate.
 *
 * Same warm ivory canvas, same architectural language as the foyer.
 * Left column carries the welcome and the setup form. Right column shows
 * a miniature Morning Brief so a new visitor sees the house they are
 * about to move into.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  AuthShell, UnderlineInput, Rule, useLightPhase,
} from "../pages/homeos/components/os/editorial";
import AuthMorningBriefPreview from "../pages/homeos/components/os/AuthMorningBriefPreview";

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { phase } = useLightPhase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A short password would not settle. Six characters or more.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("The house is ready. Let's arrange the rooms.");
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      phase={phase}
      preview={<AuthMorningBriefPreview />}
      testid="register-shell"
    >
      {/* Masthead */}
      <div className="rise-ara">
        <p className="caption-ara">ARA</p>
        <p
          className="mt-3 font-display text-[22px] leading-tight"
          style={{ fontWeight: 400, color: 'var(--ara-ink)', letterSpacing: '-0.01em' }}
          data-testid="auth-brand"
        >
          Household operating system
        </p>
      </div>

      {/* Welcome */}
      <div className="mt-16 rise-ara rise-ara-s1">
        <h1
          className="display-ara text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.02] max-w-[14ch]"
          data-testid="register-title"
        >
          Let&rsquo;s open your residence.
        </h1>
        <p className="mt-8 prose-ara max-w-[44ch] text-[17px]">
          A few quiet details, and the house will be waiting for you. You can bring
          your family in afterwards — no rush.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-16 space-y-10 rise-ara rise-ara-s2" data-testid="register-form">
        <UnderlineInput
          id="name"
          label="Your name"
          type="text"
          placeholder="Erana Fisher"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          required
          testid="name-input"
        />
        <UnderlineInput
          id="email"
          label="Email"
          type="email"
          placeholder="you@residence"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          testid="email-input"
        />
        <UnderlineInput
          id="password"
          label="A quiet password"
          type="password"
          placeholder="•••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={6}
          hint="Six characters or more. It stays here, with you."
          testid="password-input"
        />

        <div className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-4">
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 transition-opacity hover:opacity-80 disabled:opacity-40"
            style={{
              background: 'var(--ara-ink)',
              color: 'var(--ara-surface)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              minHeight: '44px',
            }}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Preparing the rooms</>
            ) : (
              <>Open the residence &nbsp;→</>
            )}
          </button>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-register-btn"
            className="link-ara"
            style={{ fontSize: '13px', letterSpacing: '0.06em', color: 'var(--ara-ink-muted)' }}
          >
            Continue with Google
          </button>
        </div>
      </form>

      {/* Divider */}
      <div className="mt-16 rise-ara rise-ara-s3">
        <Rule soft />
      </div>

      {/* Terms & sign-in link */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-10 rise-ara rise-ara-s4">
        <div>
          <p className="caption-ara">The fine print</p>
          <p className="mt-3 prose-ara text-[13px] max-w-[36ch]">
            By opening the residence you accept our terms of service and privacy
            covenant. The house keeps its own counsel.
          </p>
        </div>
        <div>
          <p className="caption-ara">Returning?</p>
          <Link
            to="/login"
            data-testid="login-link"
            className="link-ara mt-3 inline-flex"
            style={{ fontSize: '14px', letterSpacing: '0.04em' }}
          >
            Sign back into your residence
          </Link>
        </div>
      </div>

      {/* Colophon */}
      <div className="mt-24 rise-ara rise-ara-s5">
        <Rule soft />
        <p className="caption-ara mt-6">
          ARA · Set in Fraunces &amp; Inter · Printed on warm ivory
        </p>
      </div>
    </AuthShell>
  );
}
